/**
 * All user-visible texts of the web app, in Spanish (project language convention).
 * Components never hardcode strings; they read from here. Business-language only,
 * never technical errors (constitution VI). Server messages come from the shared catalogue.
 */
export const copy = {
  app: {
    titulo: 'Portal de Reembolsables',
    gobernadoPor: 'Gobernado por Pantera',
    pie: 'Portal de Reembolsables · Coninsa',
  },
  admin: {
    titulo: 'Administración',
    ingresoTitulo: 'Ingreso de administrador',
    ingresoSubtitulo:
      'Solo para el equipo de Coninsa. Los terceros entran por enlace, sin contraseña.',
    usuario: 'Usuario',
    contrasena: 'Contraseña',
    ingresar: 'Ingresar',
    ingresoPie: 'Cada ingreso queda registrado con fecha y hora.',
    salir: 'Salir',
    menu: {
      cargue: 'Cargue mensual',
      publicacion: 'Publicación',
      entregas: 'Control de entregas',
      descargas: 'Descargas',
      usuarios: 'Usuarios',
      configuracion: 'Configuración',
    },
    enConstruccion: 'Esta pantalla llega en una fase posterior.',
    rol: { operador: 'Operador', consulta: 'Consulta' },
    cambioClave: {
      titulo: 'Cambia tu contraseña',
      subtitulo:
        'Es tu primer ingreso o un operador restableció tu contraseña. Elige una nueva de al menos 12 caracteres.',
      actual: 'Contraseña actual o temporal',
      nueva: 'Contraseña nueva',
      confirmar: 'Repite la contraseña nueva',
      noCoinciden: 'Las contraseñas no coinciden.',
      guardar: 'Guardar y continuar',
    },
    usuarios: {
      titulo: 'Usuarios administradores',
      subtitulo:
        'Operadores cargan, publican y reenvían. Consulta solo ve el panel. Todo queda registrado con el usuario.',
      nuevo: 'Nuevo usuario',
      nombre: 'Nombre',
      correo: 'Correo',
      rol: 'Rol',
      estado: 'Estado',
      activo: 'Activo',
      inactivo: 'Inactivo',
      crear: 'Crear',
      desactivar: 'Desactivar',
      activar: 'Activar',
      restablecer: 'Restablecer contraseña',
      claveTemporalTitulo: 'Contraseña temporal',
      claveTemporalNota: 'Cópiala ahora y entrégala por un canal seguro. No se vuelve a mostrar.',
      soloOperador: 'Solo un operador puede gestionar usuarios.',
    },
    cargue: {
      titulo: 'Centros de costo y correos',
      subtitulo:
        'Suba el archivo del mes. Queda en base de datos y reemplaza el listado anterior. Se valida completo antes de reemplazar nada.',
      periodo: 'Periodo',
      archivo: 'Archivo (.xlsx o .csv)',
      dropTitulo: 'Arrastre el archivo o haga clic para seleccionarlo',
      dropNota:
        '.xlsx o .csv · columnas: código centro de costos, nombre centro de costos, correo 1, correo 2, …',
      origenDrive:
        'El listado se lee de la carpeta compartida en Drive cuando se envían los accesos. Esta pantalla queda como respaldo para cargarlo a mano.',
      soloOperador: 'Solo un operador puede cargar el listado.',
      aceptado: 'Archivo procesado.',
      rechazado: 'Archivo rechazado.',
      resumen: (centros: number, destinatarios: number) =>
        `${centros} centros de costo · ${destinatarios} correos`,
      ctx: {
        vigente: 'Listado vigente',
        centros: 'Centros de costo',
        correos: 'Correos',
        ultima: 'Última carga',
        cargadoPor: 'Cargado por',
      },
      sinVigente: 'Todavía no hay listado. Cargue el archivo del periodo.',
      mostrando: (n: number, total: number) => `Mostrando ${n} de ${total}`,
      verTodos: 'Ver todos',
      verMenos: 'Ver menos',
      enviarAccesos: 'Publicar y enviar accesos del periodo',
      verEntregas: 'Ver control de entregas',
      historicoTitulo: 'Histórico de cargues',
      estado: { vigente: 'Vigente', reemplazado: 'Reemplazado', rechazado: 'Rechazado' },
      origen: { manual: 'Cargue manual', drive: 'Carpeta compartida' },
      col: {
        centro: 'CECO',
        proyecto: 'Proyecto',
        tercero: 'Tercero',
        correo: 'Correos de envío',
        fecha: 'Fecha',
        origen: 'Origen',
        periodo: 'Periodo',
        archivo: 'Archivo',
        estado: 'Estado',
        usuario: 'Usuario',
      },
    },
    publicacion: {
      titulo: 'Publicación del periodo',
      subtitulo:
        'Valida los documentos del bucket, genera los paquetes comprimidos y envía los accesos. Puede tardar varios minutos.',
      periodo: 'Periodo',
      publicar: 'Publicar periodo',
      republicar: 'Republicar (solo destinatarios nuevos)',
      enviar: 'Enviar accesos del periodo',
      reenviar: 'Volver a enviar (solo destinatarios nuevos)',
      drive: {
        titulo: 'Archivo de centros de costo y correos',
        nota: 'Se lee de la carpeta compartida en Drive en el momento de enviar. Coninsa lo actualiza allá cuando cambia un correo o entra un proyecto.',
        archivo: 'Archivo',
        modificado: 'Última modificación',
        por: 'Modificado por',
        carpeta: 'Abrir carpeta',
        sinArchivo: 'La carpeta no tiene un archivo .xlsx o .csv.',
        listadoAceptado: 'Listado leído de la carpeta.',
        listadoRechazado: 'El archivo de la carpeta fue rechazado. No se envió nada.',
      },
      estadoEtiqueta: 'Estado',
      estados: {
        pendiente: 'En cola',
        validando: 'Validando documentos',
        generando: 'Generando paquetes',
        enviando: 'Enviando accesos',
        completada: 'Completada',
        fallida: 'No se pudo publicar',
      },
      paquetes: 'Centros de costo con paquete',
      correos: 'Accesos enviados',
      completada: 'Todos los destinatarios recibieron su acceso.',
      completadaParcial: 'Se enviaron los centros de costo que tienen documentos.',
      pendientes: (n: number, hasta: string) =>
        `${n} ${n === 1 ? 'centro de costo no tiene' : 'centros de costo no tienen'} documentos todavía. Se revisan una vez cada día hábil hasta el ${hasta}; cuando lleguen, se envían solos.`,
      pendientesCerrado: (n: number, hasta: string) =>
        `${n} ${n === 1 ? 'centro de costo quedó' : 'centros de costo quedaron'} sin documentos al cierre de la revisión (${hasta}). Cuando estén en el bucket, pulsa Republicar.`,
      nota: 'El proceso corre en segundo plano y continúa solo si se interrumpe. Esta pantalla se actualiza cada pocos segundos.',
      faltantes: (n: number) =>
        `${n} ${n === 1 ? 'documento del inventario ya no está' : 'documentos del inventario ya no están'} en el bucket. Los terceros no los ven. Revisa la carga y vuelve a publicar.`,
    },
    entregas: {
      titulo: 'Quién entró y quién descargó',
      subtitulo:
        'Tres estados. El del medio es el que más dice: el correo llegó y lo abrieron, pero no bajaron los documentos.',
      periodo: 'Periodo',
      exportar: 'Exportar a Excel',
      sinPublicar: 'Este periodo no se ha publicado todavía.',
      metricas: {
        enviados: 'Accesos enviados',
        descargaron: 'Descargaron',
        entraron: 'Entraron sin descargar',
        nunca: 'Nunca abrieron',
        publicados: 'Documentos publicados',
        faltantes: 'Documentos faltantes',
      },
      estados: {
        descargo: 'Descargó',
        entro_sin_descargar: 'Entró, no descargó',
        nunca_abrio: 'Nunca abrió',
        sin_documentos: 'Sin documentos aún',
      },
      rebotado: 'Rebotó',
      reenviar: 'Reenviar',
      recordar: 'Recordar',
      sinFilas: 'No hay destinatarios con ese estado.',
      mostrando: (n: number, total: number) => `Mostrando ${n} de ${total}`,
      verTodos: 'Ver todos',
      verMenos: 'Ver menos',
      col: {
        centro: 'CECO',
        tercero: 'Tercero',
        correo: 'Correo',
        docs: 'Docs',
        enviado: 'Enviado',
        ingreso: 'Ingresó',
        descarga: 'Descargó',
        estado: 'Estado',
      },
      pie: 'Cada fila es evidencia ante el tercero: cuándo se envió el acceso, cuándo entró y cuándo descargó. Un clic reenvía el acceso a quien no ha entrado.',
    },
    descargas: {
      titulo: 'Descarga del mes completo',
      subtitulo:
        'Un solo .zip con todos los lotes que el bot dejó en el bucket para el mes de cierre, organizados por centro de costo. Incluye todos los centros de costo, no solo los del listado de envío.',
      periodo: 'Mes de cierre',
      preparar: 'Preparar descarga del mes',
      prepararDeNuevo: 'Preparar de nuevo',
      descargar: 'Descargar',
      nota: 'Se arma en segundo plano y puede tardar varios minutos. Esta pantalla se actualiza sola.',
      soloOperador: 'Solo un operador puede descargar el mes completo.',
      historial: 'Meses preparados',
      contenido: (archivos: number, centros: number) =>
        `${archivos.toLocaleString('es-CO')} archivos · ${centros.toLocaleString('es-CO')} centros de costo`,
      pie: (dias: string) =>
        `Cada descarga queda disponible ${dias} días y registrada con quién la preparó y quién la descargó.`,
      estados: {
        pendiente: 'En cola',
        generando: 'Preparando',
        lista: 'Lista',
        fallida: 'No se pudo preparar',
        vencida: 'Vencida',
      },
      col: {
        mes: 'Mes',
        estado: 'Estado',
        contenido: 'Contenido',
        tamano: 'Tamaño',
        preparadoPor: 'Preparado por',
        vence: 'Disponible hasta',
      },
    },
    configuracion: {
      titulo: 'Configuración',
      vigencia: 'Vigencia de los enlaces (días)',
      vigenciaNota: 'Aplica a los accesos que se emitan después de guardar. Entre 1 y 90 días.',
      historial: 'Histórico visible para el tercero (meses)',
      historialNota:
        'Cuántos periodos hacia atrás puede consultar el tercero desde el portal, contando el vigente. Entre 1 y 24. Acordado con Coninsa: 3.',
      guardar: 'Guardar',
      guardado: 'Configuración guardada.',
    },
  },
  portal: {
    titulo: 'Soportes de reembolso',
    encabezado: (periodo: string) => `Sus soportes de ${periodo}`,
    centrosAsociados: (n: number) =>
      `${n} ${n === 1 ? 'centro de costo asociado' : 'centros de costo asociados'}`,
    descargarPeriodo: 'Descargar todo el periodo (.zip)',
    totalPeriodo: (docs: number, centros: number) =>
      `${docs} documentos · ${centros} ${centros === 1 ? 'centro de costo' : 'centros de costo'}`,
    porCentro: 'O DESCARGUE POR CENTRO DE COSTO',
    documentos: (n: number) =>
      `${n.toLocaleString('es-CO')} ${n === 1 ? 'documento' : 'documentos'}`,
    descargarZip: 'Descargar .zip',
    descargar: 'Descargar',
    verLista: 'ver archivos',
    ocultarLista: 'ocultar archivos',
    sinDocumentos: 'No hay documentos publicados para este periodo.',
    historicoTitulo: 'Periodos anteriores',
    vence: (fecha: string) =>
      `Este enlace vence el ${fecha}. Si venció, en esta misma página puede pedir uno nuevo.`,
    vencido: {
      titulo: 'Este enlace ya venció',
      subtitulo:
        'Escriba su correo y le enviamos uno nuevo al instante. No necesita escribirle a nadie de Coninsa.',
      correo: 'Su correo',
      pista: 'Debe ser un correo del listado vigente.',
      pedir: 'Enviarme un nuevo enlace',
      enviadoTitulo: 'Solicitud recibida.',
      pie: 'La solicitud queda registrada en el panel de Coninsa.',
    },
  },
  comun: {
    cargando: 'Cargando…',
    guardando: 'Guardando…',
    noEncontrado: 'La página que buscas no existe.',
    cerrar: 'Cerrar',
    cancelar: 'Cancelar',
  },
} as const;
